export interface FoodVariantRow {
  name: string;
  sku: string;
  price: number;
  costPrice: number;
  attributeValues: Record<string, string>;
  status: 'active' | 'inactive';
}

export interface FoodAddonGroupRow {
  groupCode: string;
  addonCodes: string[];
}

export interface FoodAvailabilityRow {
  day: string;
  startTime: string;
  endTime: string;
}

export interface FoodNutritionState {
  calories: number | '';
  protein: number | '';
  carbohydrates: number | '';
  fat: number | '';
  fiber: number | '';
  sugar: number | '';
  sodium: number | '';
  servingSize: string;
}

/** Matches professional Food Master table shape */
export interface FoodWizardState {
  restaurantId: string;
  branchId: string;
  foodName: string;
  foodCode: string;
  sku: string;
  categoryId: string;
  subCategoryId: string;
  cuisineId: string;
  dietaryTypeId: string;
  description: string;
  shortDescription: string;
  preparationTimeId: string;
  taxGroupId: string;
  unitId: string;
  kitchenStationId: string;
  basePrice: number;
  tagIds: string[];
  selectedAttributeCodes: string[];
  attributeValueSelections: Record<string, string[]>;
  variants: FoodVariantRow[];
  addonGroups: FoodAddonGroupRow[];
  availabilitySchedule: FoodAvailabilityRow[];
  scheduleAvailability: boolean;
  thumbnailImage: string;
  galleryImages: string[];
  isFeatured: boolean;
  isRecommended: boolean;
  isBestseller: boolean;
  displayOrder: number;
  status: 'active' | 'inactive';
  nutrition: FoodNutritionState;
}

export const FOOD_WIZARD_TABS = [
  { title: 'Basic Information', subtitle: 'Name, code, category, cuisine, dietary' },
  { title: 'Pricing & Variants', subtitle: 'Base price, attributes, variants, tax' },
  { title: 'Addons', subtitle: 'Addon groups and modifiers' },
  { title: 'Availability', subtitle: 'Preparation time and schedule' },
  { title: 'Nutrition', subtitle: 'Calories, protein, carbs, and macros per serving' },
  { title: 'Media', subtitle: 'Thumbnail and gallery' },
  { title: 'Settings', subtitle: 'Featured, recommended, bestseller, status' },
] as const;

export const DAYS = [
  { value: 'mon', label: 'Monday' },
  { value: 'tue', label: 'Tuesday' },
  { value: 'wed', label: 'Wednesday' },
  { value: 'thu', label: 'Thursday' },
  { value: 'fri', label: 'Friday' },
  { value: 'sat', label: 'Saturday' },
  { value: 'sun', label: 'Sunday' },
];

export function emptyNutrition(): FoodNutritionState {
  return {
    calories: '',
    protein: '',
    carbohydrates: '',
    fat: '',
    fiber: '',
    sugar: '',
    sodium: '',
    servingSize: '',
  };
}

export function emptyFoodWizard(restaurantId = ''): FoodWizardState {
  return {
    restaurantId,
    branchId: '',
    foodName: '',
    foodCode: '',
    sku: '',
    categoryId: '',
    subCategoryId: '',
    cuisineId: '',
    dietaryTypeId: '',
    description: '',
    shortDescription: '',
    preparationTimeId: '',
    taxGroupId: '',
    unitId: '',
    kitchenStationId: '',
    basePrice: 0,
    tagIds: [],
    selectedAttributeCodes: [],
    attributeValueSelections: {},
    variants: [],
    addonGroups: [],
    availabilitySchedule: [{ day: 'mon', startTime: '09:00', endTime: '22:00' }],
    scheduleAvailability: false,
    thumbnailImage: '',
    galleryImages: [''],
    isFeatured: false,
    isRecommended: false,
    isBestseller: false,
    displayOrder: 0,
    status: 'active',
    nutrition: emptyNutrition(),
  };
}

export function menuItemToWizard(item: Record<string, unknown>, restaurantId: string): FoodWizardState {
  const meta = (item.metadata || {}) as Record<string, unknown>;
  const food = (meta.foodMaster || meta.wizard || meta) as Partial<FoodWizardState>;
  const nutritionRaw = (item.nutrition || meta.nutrition || food.nutrition) as Record<string, unknown> | undefined;
  const numOrEmpty = (v: unknown) => (v == null || v === '' ? '' : Number(v));
  const nutrition: FoodNutritionState = {
    calories: numOrEmpty(nutritionRaw?.calories),
    protein: numOrEmpty(nutritionRaw?.protein),
    carbohydrates: numOrEmpty(nutritionRaw?.carbohydrates),
    fat: numOrEmpty(nutritionRaw?.fat),
    fiber: numOrEmpty(nutritionRaw?.fiber),
    sugar: numOrEmpty(nutritionRaw?.sugar),
    sodium: numOrEmpty(nutritionRaw?.sodium),
    servingSize: String(nutritionRaw?.servingSize || ''),
  };
  return {
    ...emptyFoodWizard(restaurantId),
    restaurantId,
    branchId: String(food.branchId || meta.branchId || ''),
    foodName: String(item.name || food.foodName || ''),
    foodCode: String(food.foodCode || meta.foodCode || ''),
    sku: String(food.sku || meta.sku || ''),
    categoryId: String(food.categoryId || meta.categoryId || item.category || ''),
    subCategoryId: String(food.subCategoryId || meta.subCategoryId || ''),
    cuisineId: String(food.cuisineId || meta.cuisineId || ''),
    dietaryTypeId: String(food.dietaryTypeId || meta.dietaryTypeId || ''),
    description: String(item.description || food.description || ''),
    shortDescription: String(food.shortDescription || ''),
    preparationTimeId: String(food.preparationTimeId || meta.preparationTimeId || ''),
    taxGroupId: String(food.taxGroupId || meta.taxGroupId || ''),
    unitId: String(food.unitId || meta.unitId || ''),
    kitchenStationId: String(food.kitchenStationId || meta.kitchenStationId || ''),
    basePrice: Number(food.basePrice ?? item.price ?? 0),
    tagIds: (food.tagIds as string[]) || (meta.tags as string[]) || [],
    selectedAttributeCodes: (food.selectedAttributeCodes as string[]) || [],
    attributeValueSelections: (food.attributeValueSelections as Record<string, string[]>) || {},
    variants: (item.variants as FoodVariantRow[]) || food.variants || [],
    addonGroups: (food.addonGroups as FoodAddonGroupRow[]) || [],
    availabilitySchedule: (food.availabilitySchedule as FoodAvailabilityRow[])?.length
      ? (food.availabilitySchedule as FoodAvailabilityRow[])
      : [{ day: 'mon', startTime: '09:00', endTime: '22:00' }],
    scheduleAvailability: Boolean(food.scheduleAvailability),
    thumbnailImage: String(item.image || food.thumbnailImage || ''),
    galleryImages: (food.galleryImages as string[])?.filter(Boolean)?.length
      ? (food.galleryImages as string[])
      : [''],
    isFeatured: Boolean(food.isFeatured),
    isRecommended: Boolean(food.isRecommended),
    isBestseller: Boolean(food.isBestseller),
    displayOrder: Number(food.displayOrder ?? meta.displayOrder ?? 0),
    status: (food.status as 'active' | 'inactive') || (meta.isActive === false ? 'inactive' : 'active'),
    nutrition,
  };
}

export function wizardToMenuPayload(form: FoodWizardState): Record<string, unknown> {
  const isVeg = ['veg', 'vegan', 'jain'].includes(form.dietaryTypeId);
  const price = form.variants.length
    ? Math.min(...form.variants.map((v) => v.price).filter((p) => p > 0), form.basePrice || Infinity)
    : form.basePrice;

  const foodMaster = { ...form };

  const nutritionPayload = Object.fromEntries(
    Object.entries(form.nutrition).filter(([, v]) => v !== '' && v != null),
  );

  return {
    name: form.foodName,
    description: form.description || form.shortDescription,
    price: Number.isFinite(price) ? price : form.basePrice,
    category: form.categoryId,
    image: form.thumbnailImage || undefined,
    variants: form.variants.filter((v) => v.name.trim()),
    addons: form.addonGroups.flatMap((g) => g.addonCodes),
    isVeg,
    isAvailable: form.status === 'active',
    sortOrder: form.displayOrder,
    ...(Object.keys(nutritionPayload).length ? { nutrition: nutritionPayload } : {}),
    metadata: {
      foodCode: form.foodCode || form.sku,
      sku: form.sku,
      foodMaster,
      branchId: form.branchId || undefined,
      categoryId: form.categoryId,
      subCategoryId: form.subCategoryId,
      cuisineId: form.cuisineId,
      dietaryTypeId: form.dietaryTypeId,
      preparationTimeId: form.preparationTimeId,
      taxGroupId: form.taxGroupId,
      unitId: form.unitId,
      kitchenStationId: form.kitchenStationId,
      tags: form.tagIds,
      galleryImages: form.galleryImages.filter(Boolean),
      availabilitySchedule: form.scheduleAvailability ? form.availabilitySchedule : undefined,
      addonGroups: form.addonGroups,
      isFeatured: form.isFeatured,
      isRecommended: form.isRecommended,
      isBestseller: form.isBestseller,
      isActive: form.status === 'active',
      nutrition: Object.keys(nutritionPayload).length ? nutritionPayload : undefined,
    },
  };
}

/** Generate variant combinations from selected attributes */
export function generateVariantsFromAttributes(
  form: FoodWizardState,
  attributeNames: Record<string, string>,
  valueNames: Record<string, string>,
): FoodVariantRow[] {
  const attrs = form.selectedAttributeCodes.filter(
    (code) => (form.attributeValueSelections[code] || []).length > 0,
  );
  if (!attrs.length) return [];

  const combos: Record<string, string>[] = [{}];
  for (const attr of attrs) {
    const values = form.attributeValueSelections[attr] || [];
    const next: Record<string, string>[] = [];
    for (const combo of combos) {
      for (const val of values) {
        next.push({ ...combo, [attr]: val });
      }
    }
    combos.length = 0;
    combos.push(...next);
  }

  return combos.map((combo) => {
    const parts = Object.entries(combo).map(([a, v]) => valueNames[v] || v);
    const name = parts.join(' ');
    const skuSuffix = Object.values(combo).join('_');
    return {
      name: `${form.foodName} ${name}`.trim(),
      sku: form.sku ? `${form.sku}_${skuSuffix}` : skuSuffix,
      price: form.basePrice,
      costPrice: 0,
      attributeValues: combo,
      status: 'active' as const,
    };
  });
}
